import React, { useState, useEffect } from "react";
import { container } from "../styles/commonStyles";
import { fetchAllWords } from '../services/wordService';
import { supabase } from '../supabaseClient';


const ProfileScreen = ({
  theme,
  setTheme,
  user,
  setUser,
  setStep,
  goHome,
  profileStats,
  weakWordsList,
  masteredWordsList,
  practicedWordsList,
  handleLogout,
  handleSaveUsername,
  handleBuyFreeze,
  canBuyFreeze,
  handleCancelSubscription
}) => {
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showSubDetails, setShowSubDetails] = useState(false);
  const [isSupportLoading, setIsSupportLoading] = useState(false);
  const [tempUsername, setTempUsername] = useState("");
  const [showDictionary, setShowDictionary] = useState(false);
  const [showWeakWords, setShowWeakWords] = useState(false);
  const [showMasteredWords, setShowMasteredWords] = useState(false);
  const [showPracticedWords, setShowPracticedWords] = useState(false);
  const [dictionarySearch, setDictionarySearch] = useState("");
  const [showResetInfo, setShowResetInfo] = useState(false);
  const [dbWords, setDbWords] = useState(null);
  const [isMapExpanded, setIsMapExpanded] = useState(false);

  useEffect(() => {
    fetchAllWords().then(setDbWords);
  }, []);

  const onSaveUsername = async () => {
    await handleSaveUsername(tempUsername);
    setIsEditingUsername(false);
  };

  const handleSupport = async () => {
    if (isSupportLoading) return;
    setIsSupportLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Please log in to support.");

      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-support-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const { checkout_url, error } = await response.json();
      if (error) throw new Error(error);
      if (checkout_url) {
        window.location.href = checkout_url;
      }
    } catch (err) {
      console.error("Support Error:", err);
      alert(err.message || "Failed to start support session. Please try again.");
    } finally {
      setIsSupportLoading(false);
    }
  };

  const isDark = theme === "dark";
  // Internal vs Display Date Logic
  const displaySubscriptionDate = React.useMemo(() => {
    if (user.is_trialing) return user.trial_end;
    return user.next_billing_date || user.subscription_end;
  }, [user.is_trialing, user.trial_end, user.next_billing_date, user.subscription_end]);

  const colors = {
    background: isDark ? "linear-gradient(180deg, #0F172A 0%, #1E293B 100%)" : "linear-gradient(180deg, #1F8A8A 0%, #4FB6B2 25%, #9FE3DE 55%, #EAF7F6 85%, #F7FCFB 100%)",
    surface: isDark ? "#1E293B" : "#FFFFFF",
    mutedSurface: isDark ? "#0F172A" : "#F8FAFC",
    text: isDark ? "#F1F5F9" : "#111827",
    secondaryText: isDark ? "#94A3B8" : "#6B7280",
    border: isDark ? "rgba(16, 185, 129, 0.1)" : "rgba(0, 0, 0, 0.05)",
    cardText: isDark ? "#F1F5F9" : "#0F172A"
  };

  return (
    <div style={{
      ...container,
      paddingTop: "24px",
      alignItems: "center",
      justifyContent: "flex-start",
      background: colors.background,
      minHeight: '100vh',
      height: 'auto',
      overflowY: 'auto',
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      paddingLeft: '16px',
      paddingRight: '16px',
      paddingBottom: '100px',
      boxSizing: 'border-box'
    }}>
      {/* Header Row */}
      <div style={{ width: "100%", maxWidth: "420px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "32px", position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            onClick={goHome}
            style={{
              width: "44px",
              height: "44px",
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(4px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              fontSize: "20px",
              color: "white"
            }}
          >
            ‹
          </button>
          <h1 style={{ margin: 0, marginLeft: "12px", fontSize: "22px", fontWeight: "700", color: "white" }}>Your Profile</h1>
        </div>

        {/* Placeholder for balance */}
        <div style={{ width: "44px" }} />
      </div>

      {/* SECTION 1: Identity Card */}
      <div style={{
        background: colors.surface,
        borderRadius: "20px",
        padding: "16px",
        marginBottom: "20px",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "16px",
        width: "100%",
        maxWidth: "420px",
        boxShadow: isDark ? "0 10px 30px rgba(0,0,0,0.2)" : "0 4px 12px rgba(0,0,0,0.05)",
        boxSizing: "border-box",
        border: isDark ? "1px solid #334155" : "none"
      }}>
        {/* Left: Avatar */}
        <div style={{
          width: "72px",
          height: "72px",
          borderRadius: "50%",
          background: "linear-gradient(135deg, #6366F1 0%, #A855F7 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "28px",
          fontWeight: "600",
          color: "white",
          boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
          border: isDark ? "4px solid #334155" : "4px solid #F3F4F6",
          flexShrink: 0
        }}>
          {user.username?.[0] || 'P'}
        </div>

        {/* Right: Content block */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          {/* Name + Edit Icon Row */}
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "22px", fontWeight: "600", color: colors.text }}>{user.username || user.fullName}</span>
            <span
              style={{ cursor: "pointer", fontSize: "16px", color: "#1AA39A", opacity: 0.7, display: "flex", alignItems: "center" }}
              onClick={() => { setTempUsername(user.username || user.fullName); setIsEditingUsername(true); }}
            >
              ✎
            </span>
          </div>

          {/* Email Text */}
          <div style={{ fontSize: "14px", fontWeight: "400", color: colors.secondaryText, marginTop: "2px" }}>
            {user.email || "priyalsethia21@gmail.com"}
          </div>

          {/* Chips Row (Streak + Plan) */}
          <div style={{ display: "flex", flexDirection: "row", gap: "8px", marginTop: "10px", flexWrap: "wrap" }}>
            {/* Streak Chip */}
            <div style={{
              background: isDark ? "rgba(20, 184, 166, 0.1)" : "#E6F6F4",
              padding: "6px 12px",
              borderRadius: "999px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "6px",
              color: isDark ? "#2DD4BF" : "#0F766E",
              fontSize: "12px",
              fontWeight: "500",
              whiteSpace: "nowrap",
              border: isDark ? "1px solid rgba(20, 184, 166, 0.2)" : "none"
            }}>
              🔥 {user.currentStreak || 0} {user.currentStreak === 1 ? 'Day' : 'Days'} Streak
            </div>
          </div>
        </div>
      </div>




      {/* SECTION 3: Progress Stats (Redesigned Word Map) */}
      <div style={{ width: "100%", maxWidth: "420px", marginBottom: "20px" }}>
        {/* Word Map Card */}
        <div
          onClick={() => setIsMapExpanded(!isMapExpanded)}
          style={{
            background: colors.surface,
            borderRadius: "28px",
            padding: "24px",
            boxShadow: isDark ? "0 15px 40px rgba(0,0,0,0.25)" : "0 6px 20px rgba(0, 0, 0, 0.04)",
            border: `1px solid ${colors.border}`,
            boxSizing: "border-box",
            cursor: "pointer",
            transition: "all 0.3s ease"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            {/* Title & Legend */}
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                <h3 style={{ fontSize: "19px", fontWeight: "900", color: colors.cardText, margin: 0 }}>Your Word Map</h3>
                <span style={{ fontSize: "12px", color: colors.secondaryText, opacity: 0.6, transform: isMapExpanded ? "rotate(180deg)" : "none", transition: "transform 0.3s ease" }}>▼</span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {[
                  { label: "Weak Words", color: "#F87171" },
                  { label: "Learning", color: "#FBBF24" },
                  { label: "Strong Words", color: "#4ADE80" },
                  { label: "New Words", color: isDark ? "#334155" : "#F1F5F9" }
                ].map(item => (
                  <div key={item.label} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ width: "12px", height: "12px", borderRadius: "3px", background: item.color }} />
                    <span style={{ fontSize: "13px", fontWeight: "600", color: colors.secondaryText }}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Heatmap Grid */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: "4px",
              padding: "4px",
              background: isDark ? "rgba(0,0,0,0.1)" : "#F8FAFC",
              borderRadius: "12px"
            }}>
              {(() => {
                const totalCells = 30;
                const totalWords = dbWords?.length || 100;

                // 1. Raw counts
                const rawCounts = {
                  new: Math.max(0, totalWords - profileStats.practiced),
                  learning: Math.max(0, profileStats.practiced - profileStats.mastered - profileStats.weak),
                  strong: profileStats.mastered,
                  weak: profileStats.weak
                };

                // 2. Guaranteed Minimums
                // Categories with > 0 items get at least 1 cell
                let cells = { new: 0, learning: 0, strong: 0, weak: 0 };
                let cellsToDistribute = totalCells;

                Object.keys(rawCounts).forEach(key => {
                  if (rawCounts[key] > 0) {
                    cells[key] = 1;
                    cellsToDistribute--;
                  }
                });

                // 3. Proportional Distribution of Remaining
                const totalRaw = Object.values(rawCounts).reduce((a, b) => a + b, 0) || 1;
                Object.keys(rawCounts).forEach(key => {
                  if (rawCounts[key] > 0) {
                    const extra = Math.floor((rawCounts[key] / totalRaw) * cellsToDistribute);
                    cells[key] += extra;
                  }
                });

                // 4. Fill Gap (rounding errors)
                let currentTotal = Object.values(cells).reduce((a, b) => a + b, 0);
                while (currentTotal < totalCells) {
                  // Add to the largest category to minimize visual impact
                  const maxKey = Object.keys(rawCounts).reduce((a, b) => rawCounts[a] > rawCounts[b] ? a : b);
                  cells[maxKey]++;
                  currentTotal++;
                }

                const cellColors = [
                  ...Array(cells.new).fill(isDark ? "#334155" : "#F1F5F9"),
                  ...Array(cells.learning).fill("#FBBF24"),
                  ...Array(cells.strong).fill("#4ADE80"),
                  ...Array(cells.weak).fill("#F87171")
                ];

                return cellColors.map((bg, i) => (
                  <div
                    key={i}
                    style={{
                      width: "18px",
                      height: "18px",
                      borderRadius: "4px",
                      background: bg,
                      transition: "background 0.5s ease"
                    }}
                  />
                ));
              })()}
            </div>
          </div>

          {/* Expanded Content: Mini Cards */}
          <div style={{
            maxHeight: isMapExpanded ? "120px" : "0",
            opacity: isMapExpanded ? 1 : 0,
            overflow: "hidden",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            marginTop: isMapExpanded ? "20px" : "0"
          }}>
            <div style={{ display: "flex", gap: "10px" }}>
              {[
                { label: "Weak", count: profileStats.weak, color: "#F87171", onClick: () => setShowWeakWords(true) },
                { label: "Learning", count: Math.max(0, profileStats.practiced - profileStats.mastered - profileStats.weak), color: "#FBBF24", onClick: () => setShowPracticedWords(true) },
                { label: "Strong", count: profileStats.mastered, color: "#4ADE80", onClick: () => setShowMasteredWords(true) }
              ].map(card => (
                <div
                  key={card.label}
                  onClick={(e) => { e.stopPropagation(); card.onClick(); }}
                  style={{
                    flex: 1,
                    background: isDark ? "rgba(255,255,255,0.03)" : "#F8FAFC",
                    borderRadius: "16px",
                    padding: "12px 8px",
                    textAlign: "center",
                    border: `1px solid ${colors.border}`,
                    cursor: "pointer"
                  }}
                >
                  <div style={{ fontSize: "18px", fontWeight: "900", color: card.color }}>{card.count}</div>
                  <div style={{ fontSize: "11px", fontWeight: "700", color: colors.secondaryText, textTransform: "uppercase", marginTop: "2px" }}>{card.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dictionary Card (Wide) */}
        <div
          onClick={() => setShowDictionary(true)}
          style={{
            background: colors.surface,
            borderRadius: "20px",
            padding: "20px 24px",
            marginTop: "16px",
            boxShadow: isDark ? "0 10px 30px rgba(0,0,0,0.15)" : "0 4px 12px rgba(0,0,0,0.03)",
            border: `1px solid ${colors.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            transition: "transform 0.2s ease"
          }}
          onMouseDown={(e) => e.currentTarget.style.transform = "scale(0.98)"}
          onMouseUp={(e) => e.currentTarget.style.transform = "scale(1)"}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "24px" }}>📗</span>
            <span style={{ fontSize: "16px", fontWeight: "800", color: colors.cardText }}>Full Dictionary</span>
          </div>
          <span style={{ fontSize: "14px", color: colors.secondaryText, fontWeight: "600" }}>›</span>
        </div>
      </div>
      {/* SECTION 6: Support Development */}
      <div style={{
        background: colors.surface,
        borderRadius: "20px",
        padding: "24px",
        marginBottom: "20px",
        width: "100%",
        maxWidth: "420px",
        boxSizing: "border-box",
        border: `1px solid ${colors.border}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        textAlign: "center",
        boxShadow: isDark ? "0 10px 30px rgba(0,0,0,0.15)" : "0 4px 12px rgba(0,0,0,0.03)"
      }}>
        <div style={{ fontSize: "32px", marginBottom: "4px" }}>☕</div>
        <h3 style={{ fontSize: "18px", fontWeight: "800", color: colors.cardText, margin: 0 }}>Support Development</h3>
        <p style={{ fontSize: "14px", color: colors.secondaryText, margin: "0 0 4px 0", lineHeight: "1.6", maxWidth: "280px" }}>
          Love using VerbalFlo?
        </p>
        <button
          onClick={handleSupport}
          disabled={isSupportLoading}
          style={{
            background: "#14B8A6",
            color: "white",
            border: "none",
            borderRadius: "14px",
            padding: "14px 28px",
            fontSize: "15px",
            fontWeight: "800",
            cursor: isSupportLoading ? "default" : "pointer",
            marginTop: "8px",
            width: "100%",
            boxShadow: "0 4px 12px rgba(20, 184, 166, 0.2)",
            transition: "all 0.2s ease",
            opacity: isSupportLoading ? 0.7 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px"
          }}
          onMouseDown={(e) => !isSupportLoading && (e.currentTarget.style.transform = "scale(0.97)")}
          onMouseUp={(e) => !isSupportLoading && (e.currentTarget.style.transform = "scale(1)")}
        >
          {isSupportLoading ? (
            <>
              <div style={{ width: "16px", height: "16px", border: "2px solid white", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
              <span>Connecting...</span>
            </>
          ) : (
            "Support VerbalFlo ☕"
          )}
        </button>
      </div>
      {/* SECTION: Streak Progress Tracker */}
      <div style={{
        background: colors.surface,
        borderRadius: "20px",
        padding: "20px 16px",
        marginBottom: "20px",
        width: "100%",
        maxWidth: "420px",
        boxShadow: isDark ? "0 10px 30px rgba(0,0,0,0.15)" : "0 4px 12px rgba(0,0,0,0.05)",
        boxSizing: "border-box",
        border: isDark ? "1px solid #334155" : "none"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", padding: "0 4px" }}>
          <span style={{ fontSize: "16px", fontWeight: "800", color: colors.cardText }}>Streak Progress</span>
          <span style={{ fontSize: "13px", fontWeight: "700", color: "#14B8A6", background: isDark ? "rgba(20, 184, 166, 0.1)" : "#F0FDFB", padding: "4px 10px", borderRadius: "99px", border: isDark ? "1px solid rgba(20, 184, 166, 0.2)" : "none" }}>
            Day {user.currentStreak || 0}
          </span>
        </div>

        <div style={{
          background: isDark ? "#0F172A" : "#F0FDFB",
          borderRadius: "16px",
          padding: "16px 8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          border: isDark ? "1px solid #334155" : "none"
        }}>
          {[0, 1, 2, 3, 4, 5, 6].map((i) => {
            const currentStreakNum = user.currentStreak || 0;
            const displayDay = currentStreakNum <= 4 ? (i + 1) : (currentStreakNum - 3 + i);
            const isPast = displayDay < currentStreakNum;
            const isToday = displayDay === currentStreakNum;

            return (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", flex: 1 }}>
                <div style={{
                  width: "30px", height: "30px", borderRadius: "50%",
                  background: isPast || isToday ? "#14B8A6" : (isDark ? "#334155" : "#E2E8F0"),
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.3s ease",
                  boxShadow: isToday ? "0 0 10px rgba(20, 184, 166, 0.3)" : "none",
                  border: isToday ? "2px solid #FFFFFF" : "none",
                  boxSizing: "border-box"
                }}>
                  {isPast || isToday ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  ) : (
                    <span style={{ fontSize: "11px", fontWeight: "800", color: colors.secondaryText }}>{displayDay}</span>
                  )}
                </div>
                <div style={{
                  fontSize: "10px",
                  fontWeight: "800",
                  color: isToday ? "#14B8A6" : colors.secondaryText,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}>
                  {isToday ? "Today" : `D${displayDay}`}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* SECTION 4: Streak Freeze */}
      <div style={{
        background: colors.mutedSurface,
        borderRadius: "20px",
        padding: "24px",
        marginBottom: "20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        maxWidth: "420px",
        boxSizing: "border-box",
        border: isDark ? "1px solid #334155" : "1px solid rgba(0,0,0,0.02)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div style={{ fontSize: "36px" }}>🧊</div>
          <div>
            <div style={{ fontSize: "16px", fontWeight: "800", color: colors.cardText, marginBottom: "2px" }}>Streak Freeze</div>
            <div style={{ fontSize: "13px", color: colors.secondaryText, maxWidth: "180px" }}>Protect your streak if you miss a day.</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div>
            <div style={{ fontSize: "24px", fontWeight: "800", color: isDark ? "#38BDF8" : "#0284C7" }}>
              {user.streakFreezes || 0} / 2
            </div>
            <div style={{ fontSize: "12px", color: colors.secondaryText, fontWeight: "700" }}>Available</div>
          </div>
        </div>
      </div>

      {/* SECTION 5: Coins Box */}
      <div style={{
        background: isDark ? "rgba(251, 191, 36, 0.05)" : "#FFFBEB",
        borderRadius: "20px",
        padding: "16px 20px",
        marginBottom: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        width: "100%",
        maxWidth: "420px",
        boxSizing: "border-box",
        border: isDark ? "1px solid rgba(251, 191, 36, 0.2)" : "1px solid #FEF3C7"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "22px" }}>🪙</span>
            <span style={{ fontSize: "16px", fontWeight: "800", color: isDark ? "#FBBF24" : "#B45309" }}>Coins Balance</span>
          </div>
          <div style={{ fontSize: "22px", fontWeight: "900", color: isDark ? "#FBBF24" : "#B45309" }}>
            {user.coins || 0}
          </div>
        </div>

        <div style={{ borderTop: isDark ? "1px solid rgba(251, 191, 36, 0.1)" : "1px solid rgba(180, 83, 9, 0.08)", paddingTop: "8px" }}>
          <div style={{ fontSize: "12px", color: isDark ? "#FBBF24" : "#B45309", opacity: 0.8, fontWeight: "600", textAlign: "center" }}>
            100 coins = 1 missed day • Recover up to 2 days
          </div>
        </div>
      </div>


      {/* SECTION: Reset Time Info */}
      <div
        onClick={() => setShowResetInfo(true)}
        style={{
          width: "100%",
          maxWidth: "420px",
          padding: "12px 16px",
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          cursor: "pointer",
          borderTop: `1px solid ${colors.border}`,
          borderBottom: `1px solid ${colors.border}`,
          opacity: 0.8
        }}
      >
        <span style={{ fontSize: "13px", fontWeight: "500", color: colors.secondaryText, textAlign: "center" }}>
          Daily streaks, workouts & rewards reset at 00:00 UTC <span style={{ fontSize: "14px", marginLeft: "4px" }}>ⓘ</span>
        </span>
      </div>

      {/* Logout Button */}
      <button
        onClick={handleLogout}
        style={{
          width: "100%",
          maxWidth: "420px",
          background: isDark ? "rgba(225, 29, 72, 0.1)" : "#FFF1F2",
          color: "#E11D48",
          border: isDark ? "1px solid rgba(225, 29, 72, 0.2)" : "none",
          padding: "18px",
          borderRadius: "18px",
          fontWeight: "800",
          fontSize: "17px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          cursor: "pointer",
          marginBottom: "24px"
        }}
      >
        <span style={{ fontSize: "20px" }}>↪</span> Logout
      </button>

      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "10px",
        color: colors.secondaryText,
        fontSize: "12px",
        paddingBottom: "60px",
        textAlign: "center",
        width: "100%",
        maxWidth: "420px"
      }}>
        <span
          onClick={() => window.open("https://forms.gle/79nhW9MwhdERg1Hq6", "_blank", "noopener,noreferrer")}
          style={{ cursor: "pointer", fontWeight: "600", color: colors.secondaryText, textDecoration: "none" }}
        >
          Contact Us
        </span>
        <span style={{ opacity: 0.3 }}>•</span>
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ fontSize: "14px" }}>🔒</span> Your data is safe and secure with us.
        </span>
      </div>

      {/* MODALS INSIDE PROFILE STEP */}
      {showResetInfo && (
        <div
          onClick={() => setShowResetInfo(false)}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: isDark ? "rgba(0, 0, 0, 0.8)" : "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", zIndex: 2000,
            display: "flex", alignItems: "center", justifyContent: "center", padding: "24px"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: colors.surface,
              borderRadius: "28px",
              padding: "32px",
              width: "100%",
              maxWidth: "400px",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
              position: "relative",
              border: isDark ? "1px solid #334155" : "none"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: "800", color: colors.text, margin: 0 }}>Reset Time by Region</h2>
              <button
                onClick={() => setShowResetInfo(false)}
                style={{ background: "none", border: "none", fontSize: "24px", color: colors.secondaryText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                ✕
              </button>
            </div>

            <p style={{ fontSize: "14px", color: colors.secondaryText, marginBottom: "16px" }}>00:00 UTC equals approximately:</p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "8px 16px", fontSize: "14px", color: colors.text, fontWeight: "500" }}>
              <span>UK (London):</span> <span style={{ fontWeight: "700" }}>00:00</span>
              <span>Europe (Berlin):</span> <span style={{ fontWeight: "700" }}>01:00</span>
              <span>Middle East (Dubai):</span> <span style={{ fontWeight: "700" }}>04:00</span>
              <span>India (IST):</span> <span style={{ fontWeight: "700" }}>05:30</span>
              <span>Singapore / China:</span> <span style={{ fontWeight: "700" }}>08:00</span>
              <span>Japan / Korea:</span> <span style={{ fontWeight: "700" }}>09:00</span>
              <span>Australia (Sydney):</span> <span style={{ fontWeight: "700" }}>10:00</span>
              <span>US Eastern (NY):</span> <span style={{ fontWeight: "700" }}>20:00 (prev day)</span>
              <span>US Pacific (LA):</span> <span style={{ fontWeight: "700" }}>17:00 (prev day)</span>
            </div>

            <div style={{ borderTop: `1px solid ${colors.border}`, marginTop: "24px", paddingTop: "16px", fontSize: "12px", color: colors.secondaryText, fontStyle: "italic", textAlign: "center" }}>
              Times may vary seasonally due to daylight saving.
            </div>
          </div>
        </div>
      )}

      {isEditingUsername && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: isDark ? "rgba(0, 0, 0, 0.8)" : "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", zIndex: 2000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: "24px"
        }}>
          <div style={{ background: colors.surface, borderRadius: "28px", padding: "32px", width: "100%", maxWidth: "400px", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)", border: isDark ? "1px solid #334155" : "none" }}>
            <h2 style={{ fontSize: "24px", fontWeight: "800", color: colors.text, marginBottom: "8px", marginTop: 0 }}>Edit Username</h2>
            <p style={{ fontSize: "15px", color: colors.secondaryText, marginBottom: "24px" }}>Choose a name that other players will see.</p>

            <input
              type="text"
              value={tempUsername}
              onChange={(e) => setTempUsername(e.target.value)}
              placeholder="Enter new username"
              autoFocus
              style={{ width: "100%", padding: "16px 20px", borderRadius: "16px", border: isDark ? "2px solid #334155" : "2px solid #F1F5F9", outline: "none", fontSize: "16px", background: colors.mutedSurface, color: colors.text, marginBottom: "24px", boxSizing: "border-box" }}
            />

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => setIsEditingUsername(false)}
                style={{ flex: 1, padding: "16px", borderRadius: "16px", border: "none", background: isDark ? "#334155" : "#F1F5F9", color: colors.secondaryText, fontWeight: "700", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={onSaveUsername}
                style={{ flex: 1, padding: "16px", borderRadius: "16px", border: "none", background: "#075985", color: "white", fontWeight: "700", cursor: "pointer" }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showDictionary && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: isDark ? "#0F172A" : "white", zIndex: 1001,
          padding: "0 24px 20px 24px", overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center"
        }}>
          <div style={{ position: "sticky", top: 0, background: isDark ? "#0F172A" : "white", width: "100%", textAlign: "center", paddingTop: "20px", paddingBottom: "20px", display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", zIndex: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%", maxWidth: "420px", alignItems: "center" }}>
              <h2 style={{ fontSize: "24px", fontWeight: "800", color: colors.text, margin: 0 }}>Full Dictionary</h2>
              <button
                onClick={() => { setShowDictionary(false); setDictionarySearch(""); }}
                style={{ background: isDark ? "#1E293B" : "#F1F5F9", color: colors.text, border: "none", borderRadius: "50%", width: "36px", height: "36px", cursor: "pointer", fontWeight: "800", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                ✕
              </button>
            </div>
            <input
              type="text"
              placeholder="Search all words..."
              value={dictionarySearch}
              onChange={(e) => setDictionarySearch(e.target.value)}
              style={{ width: "100%", maxWidth: "420px", padding: "14px 18px", borderRadius: "16px", border: isDark ? "2px solid #334155" : "2px solid #F1F5F9", outline: "none", fontSize: "16px", background: isDark ? "#1E293B" : "#F8FAFC", color: colors.text }}
            />
          </div>

          <div style={{ width: "100%", maxWidth: "420px", display: "flex", flexDirection: "column", gap: "12px", marginTop: "10px" }}>
            {dbWords
              ? dbWords
                .filter(w => w.word.toLowerCase().includes(dictionarySearch.toLowerCase()))
                .map((w, i) => (
                  <div key={i} style={{ background: isDark ? "#1E293B" : "#F8FAFC", padding: "20px", borderRadius: "20px", display: "flex", flexDirection: "column", gap: "4px", border: isDark ? "1px solid #334155" : "1px solid rgba(0,0,0,0.03)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <div style={{ fontWeight: "800", fontSize: "18px", color: colors.text }}>{w.word}</div>
                      <div style={{ fontSize: "11px", color: colors.secondaryText, fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>{w.group ? `Group ${w.group}` : 'New Word'}</div>
                    </div>
                    <div style={{ fontSize: "14px", color: colors.secondaryText, lineHeight: "1.5" }}>{w.meaning}</div>
                  </div>
                ))
              : <div style={{ textAlign: 'center', color: colors.secondaryText }}>Loading words...</div>
            }
          </div>
        </div>
      )}

      {showWeakWords && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: isDark ? "#0F172A" : "white", zIndex: 1001,
          padding: "0 24px 20px 24px", overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center"
        }}>
          <div style={{ position: "sticky", top: 0, background: isDark ? "#0F172A" : "white", width: "100%", textAlign: "center", paddingTop: "20px", paddingBottom: "20px", display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", zIndex: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%", maxWidth: "420px", alignItems: "center" }}>
              <h2 style={{ fontSize: "24px", fontWeight: "800", color: isDark ? "#FB923C" : "#EA580C", margin: 0 }}>Weak Words</h2>
              <button
                onClick={() => { setShowWeakWords(false); setDictionarySearch(""); }}
                style={{ background: isDark ? "rgba(234, 88, 12, 0.1)" : "#FFF7ED", color: isDark ? "#FB923C" : "#EA580C", border: "none", borderRadius: "50%", width: "36px", height: "36px", cursor: "pointer", fontWeight: "800", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                ✕
              </button>
            </div>
            <input
              type="text"
              placeholder={`Search ${weakWordsList.length} weak words...`}
              value={dictionarySearch}
              onChange={(e) => setDictionarySearch(e.target.value)}
              style={{ width: "100%", maxWidth: "420px", padding: "14px 18px", borderRadius: "16px", border: isDark ? "2px solid rgba(234, 88, 12, 0.2)" : "2px solid #FFF7ED", outline: "none", fontSize: "16px", background: isDark ? "rgba(234, 88, 12, 0.1)" : "#FFF7ED", color: colors.text }}
            />
          </div>
          <div style={{ width: "100%", maxWidth: "420px", display: "flex", flexDirection: "column", gap: "12px", marginTop: "10px" }}>
            {weakWordsList.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: colors.secondaryText }}>No weak words! You're doing great. 🚀</div>
            ) : (
              weakWordsList
                .filter(w => w.word.toLowerCase().includes(dictionarySearch.toLowerCase()))
                .map((w, i) => (
                  <div key={i} style={{ background: isDark ? "rgba(234, 88, 12, 0.1)" : "#FFF7ED", padding: "20px", borderRadius: "20px", display: "flex", flexDirection: "column", gap: "4px", border: isDark ? "1px solid rgba(234, 88, 12, 0.2)" : "1px solid rgba(234,88,12,0.1)" }}>
                    <div style={{ fontWeight: "800", fontSize: "18px", color: isDark ? "#FB923C" : "#EA580C" }}>{w.word}</div>
                    <div style={{ fontSize: "14px", color: isDark ? "#FB923C" : "#9A3412", lineHeight: "1.5" }}>{w.meaning}</div>
                  </div>
                ))
            )}
          </div>
        </div>
      )}

      {showMasteredWords && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: isDark ? "#0F172A" : "white", zIndex: 1001,
          padding: "0 24px 20px 24px", overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center"
        }}>
          <div style={{ position: "sticky", top: 0, background: isDark ? "#0F172A" : "white", width: "100%", textAlign: "center", paddingTop: "20px", paddingBottom: "20px", display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", zIndex: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%", maxWidth: "420px", alignItems: "center" }}>
              <h2 style={{ fontSize: "24px", fontWeight: "800", color: isDark ? "#4ADE80" : "#16A34A", margin: 0 }}>Mastered Words</h2>
              <button
                onClick={() => { setShowMasteredWords(false); setDictionarySearch(""); }}
                style={{ background: isDark ? "rgba(22, 163, 74, 0.1)" : "#F0FDF4", color: isDark ? "#4ADE80" : "#16A34A", border: "none", borderRadius: "50%", width: "36px", height: "36px", cursor: "pointer", fontWeight: "800", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                ✕
              </button>
            </div>
            <input
              type="text"
              placeholder={`Search ${masteredWordsList.length} mastered words...`}
              value={dictionarySearch}
              onChange={(e) => setDictionarySearch(e.target.value)}
              style={{ width: "100%", maxWidth: "420px", padding: "14px 18px", borderRadius: "16px", border: isDark ? "2px solid rgba(22, 163, 74, 0.2)" : "2px solid #F0FDF4", outline: "none", fontSize: "16px", background: isDark ? "rgba(22, 163, 74, 0.1)" : "#F0FDF4", color: colors.text }}
            />
          </div>
          <div style={{ width: "100%", maxWidth: "420px", display: "flex", flexDirection: "column", gap: "12px", marginTop: "10px" }}>
            {masteredWordsList.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: colors.secondaryText }}>No mastered words yet. Keep practicing! 🎯</div>
            ) : (
              masteredWordsList
                .filter(w => w.word.toLowerCase().includes(dictionarySearch.toLowerCase()))
                .map((w, i) => (
                  <div key={i} style={{ background: isDark ? "rgba(22, 163, 74, 0.1)" : "#F0FDF4", padding: "20px", borderRadius: "20px", display: "flex", flexDirection: "column", gap: "4px", border: isDark ? "1px solid rgba(22, 163, 74, 0.2)" : "1px solid rgba(22,163,74,0.1)" }}>
                    <div style={{ fontWeight: "800", fontSize: "18px", color: isDark ? "#4ADE80" : "#16A34A" }}>{w.word}</div>
                    <div style={{ fontSize: "14px", color: isDark ? "#4ADE80" : "#166534", lineHeight: "1.5" }}>{w.meaning}</div>
                  </div>
                ))
            )}
          </div>
        </div>
      )}

      {showPracticedWords && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: isDark ? "#0F172A" : "white", zIndex: 1001,
          padding: "0 24px 20px 24px", overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center"
        }}>
          <div style={{ position: "sticky", top: 0, background: isDark ? "#0F172A" : "white", width: "100%", textAlign: "center", paddingTop: "20px", paddingBottom: "20px", display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", zIndex: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%", maxWidth: "420px", alignItems: "center" }}>
              <h2 style={{ fontSize: "24px", fontWeight: "800", color: isDark ? "#38BDF8" : "#0284C7", margin: 0 }}>Practiced Words</h2>
              <button
                onClick={() => { setShowPracticedWords(false); setDictionarySearch(""); }}
                style={{ background: isDark ? "rgba(2, 132, 199, 0.1)" : "#F0F9FF", color: isDark ? "#38BDF8" : "#0284C7", border: "none", borderRadius: "50%", width: "36px", height: "36px", cursor: "pointer", fontWeight: "800", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                ✕
              </button>
            </div>
            <input
              type="text"
              placeholder={`Search ${practicedWordsList.length} practiced words...`}
              value={dictionarySearch}
              onChange={(e) => setDictionarySearch(e.target.value)}
              style={{ width: "100%", maxWidth: "420px", padding: "14px 18px", borderRadius: "16px", border: isDark ? "2px solid rgba(2, 132, 199, 0.2)" : "2px solid #F0F9FF", outline: "none", fontSize: "16px", background: isDark ? "rgba(2, 132, 199, 0.1)" : "#F0F9FF", color: colors.text }}
            />
          </div>
          <div style={{ width: "100%", maxWidth: "420px", display: "flex", flexDirection: "column", gap: "12px", marginTop: "10px" }}>
            {practicedWordsList.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: colors.secondaryText }}>No practiced words yet. Start a game! 🎮</div>
            ) : (
              practicedWordsList
                .filter(w => w.word.toLowerCase().includes(dictionarySearch.toLowerCase()))
                .map((w, i) => (
                  <div key={i} style={{ background: isDark ? "rgba(2, 132, 199, 0.1)" : "#F0F9FF", padding: "20px", borderRadius: "20px", display: "flex", flexDirection: "column", gap: "4px", border: isDark ? "1px solid rgba(2, 132, 199, 0.2)" : "1px solid rgba(2,132,199,0.1)" }}>
                    <div style={{ fontWeight: "800", fontSize: "18px", color: isDark ? "#38BDF8" : "#0284C7" }}>{w.word}</div>
                    <div style={{ fontSize: "14px", color: isDark ? "#38BDF8" : "#075985", lineHeight: "1.5" }}>{w.meaning}</div>
                  </div>
                ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileScreen;
